import React, { useState } from 'react';
import {
  Sparkles,
  Wand2,
  RefreshCw,
  Hash,
  Copy,
  Check,
  Loader2,
  ChevronDown,
  Zap,
  MessageSquare,
  Video
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { api, isUpgradeError } from '../context/AuthContext';
import { CreditCost } from './CreditMeter';
import useCredits, { creditCost } from '../hooks/useCredits';
import { toast } from 'sonner';

const TONES = [
  { id: 'professional', name: 'Professional', emoji: '💼' },
  { id: 'casual', name: 'Casual', emoji: '😊' },
  { id: 'funny', name: 'Funny', emoji: '😄' },
  { id: 'inspirational', name: 'Inspirational', emoji: '✨' },
  { id: 'educational', name: 'Educational', emoji: '📚' },
  { id: 'promotional', name: 'Promotional', emoji: '🎯' },
];

const LENGTHS = [
  { id: 'short', name: 'Short', description: 'Under 100 chars' },
  { id: 'medium', name: 'Medium', description: '100-280 chars' },
  { id: 'long', name: 'Long', description: '280-500 chars' },
];

const VIDEO_SIZES = [
  { id: '1280x720', name: 'Landscape (16:9)', description: 'YouTube, Facebook' },
  { id: '1024x1024', name: 'Square (1:1)', description: 'Instagram, Facebook' },
  { id: '1024x1792', name: 'Portrait (9:16)', description: 'TikTok, Reels, Stories' },
  { id: '1792x1024', name: 'Widescreen', description: 'Cinematic' },
];

const VIDEO_DURATIONS = [
  { id: 4, name: '4 seconds', description: 'Quick teaser' },
  { id: 8, name: '8 seconds', description: 'Short clip' },
  { id: 12, name: '12 seconds', description: 'Full video' },
];

export const AICaptionGenerator = ({
  currentContent,
  platform,
  onApplyCaption,
  onApplyHashtags
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmoji, setIncludeEmoji] = useState(true);
  const [generatedCaption, setGeneratedCaption] = useState('');
  const [generatedHashtags, setGeneratedHashtags] = useState([]);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!topic && !currentContent) {
      toast.error('Please enter a topic or have existing content');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/generate-caption', {
        topic: topic || undefined,
        existing_content: currentContent || undefined,
        platform: platform || 'general',
        tone,
        length,
        include_hashtags: includeHashtags,
        include_emoji: includeEmoji
      });

      setGeneratedCaption(response.data.caption);
      setGeneratedHashtags(response.data.hashtags || []);
      
      if (response.data.source === 'mock') {
        toast.info('Using demo AI generation');
      } else {
        toast.success('Caption generated!');
      }
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to generate caption');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (generatedCaption) {
      onApplyCaption(generatedCaption);
    }
    if (generatedHashtags.length > 0 && onApplyHashtags) {
      onApplyHashtags(generatedHashtags);
    }
    setOpen(false);
    toast.success('Caption applied!');
  };

  const handleCopy = () => {
    const fullText = generatedCaption + (generatedHashtags.length > 0 
      ? '\n\n' + generatedHashtags.join(' ') 
      : '');
    navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-purple-200 text-purple-600 hover:bg-purple-50"
          data-testid="ai-caption-btn"
        >
          <Sparkles className="h-4 w-4" />
          AI Write
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0 max-h-[85vh] overflow-y-auto" align="start" side="bottom" sideOffset={5}>
        <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-purple-500" />
            AI Caption Generator
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Generate engaging captions with AI
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* Topic Input */}
          <div className="space-y-2">
            <Label>What&apos;s your post about?</Label>
            <Input
              placeholder="e.g., New product launch, Behind the scenes..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="ai-topic-input"
            />
          </div>

          {/* Tone Selection */}
          <div className="space-y-2">
            <Label>Tone</Label>
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTone(t.id)}
                  className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-all ${
                    tone === t.id
                      ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <span>{t.emoji}</span>
                  <span>{t.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Length Selection */}
          <div className="space-y-2">
            <Label>Length</Label>
            <Select value={length} onValueChange={setLength}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LENGTHS.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    <div className="flex items-center gap-2">
                      <span>{l.name}</span>
                      <span className="text-xs text-slate-400">({l.description})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Options */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeHashtags}
                onChange={(e) => setIncludeHashtags(e.target.checked)}
                className="rounded"
              />
              Include hashtags
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeEmoji}
                onChange={(e) => setIncludeEmoji(e.target.checked)}
                className="rounded"
              />
              Include emoji
            </label>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            data-testid="generate-caption-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Caption
                <CreditCost feature="caption" />
              </>
            )}
          </Button>

          {/* Generated Result */}
          {generatedCaption && (
            <div className="space-y-3 pt-3 border-t">
              <div className="relative">
                <Textarea
                  value={generatedCaption}
                  onChange={(e) => setGeneratedCaption(e.target.value)}
                  className="min-h-[100px] pr-10"
                  data-testid="generated-caption"
                />
                <button
                  onClick={handleCopy}
                  className="absolute top-2 right-2 p-1.5 rounded hover:bg-slate-100"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-slate-400" />
                  )}
                </button>
              </div>

              {/* Generated Hashtags */}
              {generatedHashtags.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Suggested Hashtags</Label>
                  <div className="flex flex-wrap gap-1">
                    {generatedHashtags.map((tag, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="cursor-pointer hover:bg-purple-100"
                        onClick={() => {
                          const newTags = generatedHashtags.filter((_, i) => i !== index);
                          setGeneratedHashtags(newTags);
                        }}
                      >
                        {tag.startsWith('#') ? tag : `#${tag}`}
                        <span className="ml-1 text-slate-400">×</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={handleGenerate}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                <Button
                  onClick={handleApply}
                  size="sm"
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  data-testid="apply-caption-btn"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Apply Caption
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};


// Hashtag Generator Component
export const AIHashtagGenerator = ({ content, platform, onApplyHashtags }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState('mixed');
  const [count, setCount] = useState(10);
  const [hashtags, setHashtags] = useState([]);
  const [selectedHashtags, setSelectedHashtags] = useState([]);

  const handleGenerate = async () => {
    if (!content) {
      toast.error('Please add some content first');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/generate-hashtags', {
        content,
        platform: platform || 'general',
        count,
        style
      });

      setHashtags(response.data.hashtags);
      setSelectedHashtags(response.data.hashtags.map(h => h.tag || h));
      toast.success('Hashtags generated!');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to generate hashtags');
    } finally {
      setLoading(false);
    }
  };

  const toggleHashtag = (tag) => {
    setSelectedHashtags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleApply = () => {
    if (selectedHashtags.length > 0) {
      onApplyHashtags(selectedHashtags);
      setOpen(false);
      toast.success(`${selectedHashtags.length} hashtags applied!`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid="ai-hashtag-btn"
        >
          <Hash className="h-4 w-4" />
          AI Hashtags
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-cyan-50">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Hash className="h-5 w-5 text-blue-500" />
            AI Hashtag Generator
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Generate trending & relevant hashtags
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* Style Selection */}
          <div className="space-y-2">
            <Label>Hashtag Style</Label>
            <div className="flex gap-2">
              {[
                { id: 'popular', name: 'Popular', desc: 'High reach' },
                { id: 'niche', name: 'Niche', desc: 'Targeted' },
                { id: 'mixed', name: 'Mixed', desc: 'Best of both' }
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm transition-all ${
                    style === s.id
                      ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs opacity-70">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Count */}
          <div className="space-y-2">
            <Label>Number of hashtags: {count}</Label>
            <input
              type="range"
              min="5"
              max="20"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading || !content}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Generate Hashtags
                <CreditCost feature="hashtags" />
              </>
            )}
          </Button>

          {/* Generated Hashtags */}
          {hashtags.length > 0 && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex flex-wrap gap-2">
                {hashtags.map((h, index) => {
                  const tag = h.tag || h;
                  const isSelected = selectedHashtags.includes(tag);
                  return (
                    <button
                      key={index}
                      onClick={() => toggleHashtag(tag)}
                      className={`px-2 py-1 rounded-full text-sm transition-all ${
                        isSelected
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {tag.startsWith('#') ? tag : `#${tag}`}
                      {h.type && (
                        <span className={`ml-1 text-xs ${isSelected ? 'opacity-70' : 'text-slate-400'}`}>
                          {h.type === 'popular' ? '🔥' : '🎯'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-500">
                <span>{selectedHashtags.length} selected</span>
                <Button
                  onClick={handleApply}
                  size="sm"
                  disabled={selectedHashtags.length === 0}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Apply Selected
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};


// Video Generator Component
export const AIVideoGenerator = ({ onVideoCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1280x720');
  const [duration, setDuration] = useState(4);
  const { credits, prices } = useCredits();
  const videoCost = creditCost(prices, 'video_generation', { seconds: Math.min(duration, 10) });
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the video you want to create');
      return;
    }

    setLoading(true);
    setJobStatus(null);
    setProgress(0);

    try {
      const response = await api.post('/ai/generate-video', {
        prompt: prompt.trim(),
        duration,
        size,
        model: 'sora-2'
      });

      setJobId(response.data.job_id);
      setJobStatus('processing');
      toast.success('Video generation started!');

      // Start polling for status
      pollStatus(response.data.job_id);
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to start video generation');
      setLoading(false);
    }
  };

  const pollStatus = async (id) => {
    const checkStatus = async () => {
      try {
        const response = await api.get(`/ai/video-status/${id}`);
        const status = response.data;

        setJobStatus(status.status);
        setProgress(status.progress || 0);

        if (status.status === 'completed') {
          setLoading(false);
          toast.success('Video generated successfully!');
          if (onVideoCreated) {
            onVideoCreated(status.output_url);
          }
        } else if (status.status === 'failed') {
          setLoading(false);
          toast.error(`Video generation failed: ${status.error}`);
        } else {
          // Continue polling
          setTimeout(() => checkStatus(), 5000);
        }
      } catch (error) {
        setLoading(false);
        toast.error('Failed to check video status');
      }
    };

    checkStatus();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
          data-testid="ai-video-btn"
        >
          <Video className="h-4 w-4" />
          AI Video
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-indigo-500" />
            AI Video Generator
            <Badge variant="secondary" className="ml-2">Sora 2</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Prompt */}
          <div className="space-y-2">
            <Label>Describe your video</Label>
            <Textarea
              placeholder="e.g., A golden retriever playing fetch in a sunny park, cinematic slow motion..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[100px]"
              data-testid="video-prompt-input"
            />
            <p className="text-xs text-slate-500">
              Be specific about the scene, action, style, and mood you want.
            </p>
          </div>

          {/* Size Selection */}
          <div className="space-y-2">
            <Label>Video Format</Label>
            <div className="grid grid-cols-2 gap-2">
              {VIDEO_SIZES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSize(s.id)}
                  className={`p-3 rounded-lg text-left transition-all ${
                    size === s.id
                      ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs opacity-70">{s.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Duration Selection */}
          <div className="space-y-2">
            <Label>Duration</Label>
            <div className="flex gap-2">
              {VIDEO_DURATIONS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDuration(d.id)}
                  className={`flex-1 p-2 rounded-lg text-center transition-all ${
                    duration === d.id
                      ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <div className="font-medium text-sm">{d.name}</div>
                  <div className="text-xs opacity-70">{d.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Progress */}
          {loading && (
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {jobStatus === 'processing' ? 'Generating video...' : 'Starting...'}
                </span>
                <span className="text-indigo-600 font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-slate-500">
                This may take 2-5 minutes depending on video length.
              </p>
            </div>
          )}

          {/* Completed Video */}
          {jobStatus === 'completed' && jobId && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <Check className="h-5 w-5" />
                <span className="font-medium">Video Ready!</span>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/video/${jobId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-green-600 underline"
              >
                Download Video
              </a>
            </div>
          )}
        </div>

        {videoCost != null && credits && !credits.unlimited && (
          <p className="text-xs text-slate-500 text-right" data-testid="video-cost-estimate">
            This video uses {videoCost} of your {credits.balance.toLocaleString()} credits
            (refunded if generation fails).
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
            data-testid="generate-video-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Video className="h-4 w-4 mr-2" />
                Generate Video
                <CreditCost feature="video_generation" seconds={Math.min(duration, 10)} />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default { AICaptionGenerator, AIHashtagGenerator, AIVideoGenerator };
